import { Link } from "react-router-dom";
import {
  ButtonDropdown,
  Icon,
  Input,
  Popover,
  TableProps,
} from "@amzn/awsui-components-react";
import { StagedFile } from "./definitions";
import { createLabelFunction } from "utils/table-utils";
import { formatLink } from "utils/url-utils";
import { getFileExtension } from "utils/date-format";
import styles from "../documents-table/style.module.scss";
import { WizardColumnDefinitionsProps } from "./definitions";

export const getWizardDocFileName = (
  item: StagedFile,
  docToRename: StagedFile | undefined,
  fileName: string,
  setFileName: (fileName: string) => void,
  setDocToRename: (doc: StagedFile | undefined) => void,
  changeDocFileName: (doc: StagedFile) => void,
  downloadSingleDoc: (stagedFile: StagedFile) => void,
  isCreateMode: boolean
) => {
  if (item.id === docToRename?.id) {
    // Get file extension for display
    const fileExtension = item.file
      ? getFileExtension(item.file.name)
      : item.originalDocument?.fileName
      ? getFileExtension(item.originalDocument.extentionType)
      : "";

    return (
      <div className={styles.renameInline}>
        <div className={styles.renameInput}>
          <Input
            onChange={({ detail }) => setFileName(detail.value)}
            value={fileName}
          />
        </div>
        {fileExtension && (
          <span className={styles.fileExtension}>{fileExtension}</span>
        )}
        <span
          onClick={() => {
            setDocToRename(undefined);
          }}
          className={styles.renameButton}
        >
          <Icon name="close"></Icon>
        </span>
        <span
          onClick={() => {
            changeDocFileName(docToRename);
          }}
          className={styles.renameButton}
        >
          <Icon name="check"></Icon>
        </span>
      </div>
    );
  } else {
    // Get the full filename with extension for display
    const getFullFileName = () => {
      if (item.link) {
        return item.fileName; // Links don't have extensions
      } else if (item.file) {
        // For new files, get extension from the File object
        const extension = getFileExtension(item.file.name);
        return `${item.fileName}${extension}`;
      } else if (item.originalDocument?.fileName) {
        // For existing files, get extension from original document
        const extension = getFileExtension(item.originalDocument.extentionType);
        return `${item.fileName}${extension}`;
      }
      return item.fileName;
    };

    const fullFileName = getFullFileName();

    if (item.link) {
      // This is a link
      return (
        <div className={styles.fileName}>
          <Popover
            dismissButton={false}
            content={
              <a
                href={formatLink(item.link)}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.fileName}
              >
                {item.link} <Icon name="external" />
              </a>
            }
          >
            {fullFileName}
          </Popover>
        </div>
      );
    } else if (item.file || (item.originalDocument && !isCreateMode)) {
      // This is a file - show download link only if not in create mode and has original document
      if (isCreateMode || !item.originalDocument) {
        return <span className={styles.fileName}>{fullFileName}</span>;
      } else {
        return (
          <Link
            to="#"
            className={styles.fileName}
            onClick={() => downloadSingleDoc(item)}
          >
            {fullFileName}
            <Icon name="download" />
          </Link>
        );
      }
    } else {
      return <span className={styles.fileName}>{fullFileName}</span>;
    }
  }
};

export const createWizardColumnDefinitions = (
  props: WizardColumnDefinitionsProps
): TableProps.ColumnDefinition<StagedFile>[] => [
  {
    id: "fileName",
    header: "Name",
    cell: (item) =>
      getWizardDocFileName(
        item,
        props.docToRename,
        props.fileName,
        props.setFileName,
        props.setDocToRename,
        props.changeDocFileName,
        props.downloadSingleDoc,
        props.isCreateMode
      ),
    ariaLabel: createLabelFunction("fileName"),
    sortingField: "fileName",
    isRowHeader: true,
    width: "40%",
  },
  {
    id: "size",
    header: "Size",
    cell: (item) => {
      if (item.file) {
        // Format file size
        const bytes = item.file.size;
        if (bytes === 0) return "0 Bytes";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
      } else if (item.originalDocument?.size) {
        return item.originalDocument.size;
      }
      return "—";
    },
    width: "20%",
  },
  {
    id: "type",
    header: "Type",
    cell: (item) => {
      // Show document type (business category)
      const docTypeOption = props.uiContent.documentTypeOptions?.find(
        (option: { value: string; label: string }) =>
          option.value === item.documentType
      );
      return docTypeOption ? docTypeOption.label : item.documentType || "—";
    },
    width: "20%",
  },
  {
    id: "actions",
    header: "Actions",
    cell: (item) => {
      const actions = [];

      // Download action - only for existing files with s3Prefix
      if (
        !props.isCreateMode &&
        item.originalDocument?.s3Prefix &&
        !item.toDelete
      ) {
        actions.push({
          id: "download",
          text: "Download",
        });
      }

      // Rename action - always available unless marked for deletion
      if (!item.toDelete) {
        actions.push({
          id: "rename",
          text: "Rename",
        });
      }

      // Delete/Remove action
      if (item.toDelete) {
        actions.push({
          id: "restore",
          text: "Restore",
        });
      } else {
        actions.push({
          id: "delete",
          text: item.isNew ? "Remove" : "Delete",
        });
      }

      return (
        <div>
          <ButtonDropdown
            data-testid="wizard-document-button-dropdown"
            items={actions}
            variant="icon"
            onItemClick={(event) => props.handleItemActionClick(event, item)}
            expandToViewport
          />
        </div>
      );
    },
    width: "20%",
  },
];
